/**
 * ``useTheme`` — stateful theme selection bound to ``localStorage`` + the document root.
 *
 * Changing the theme persists it and re-applies the ``data-theme`` attribute; while on
 * ``system`` it also live-reacts to OS colour-scheme changes.
 */
import { useCallback, useEffect, useState } from "react";

import { applyTheme, loadTheme, saveTheme, type Theme } from "./model";

export interface ThemeState {
  readonly theme: Theme;
  readonly setTheme: (theme: Theme) => void;
}

export function useTheme(): ThemeState {
  const [theme, setThemeState] = useState<Theme>(() => loadTheme());

  useEffect(() => {
    applyTheme(theme);
    saveTheme(theme);
  }, [theme]);

  // While following the system theme, re-apply when the OS preference flips.
  useEffect(() => {
    if (
      theme !== "system" ||
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined;
    }
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (): void => {
      applyTheme("system");
    };
    media.addEventListener("change", handler);
    return () => {
      media.removeEventListener("change", handler);
    };
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  return { theme, setTheme };
}
